import { CURRENCY, OFFER_REQUEST_LEAD_SEC, OFFER_REQUEST_TIMEOUT_MS, activeCues, } from '@apm/contracts';
export class OfferCues {
    offers;
    host;
    /**
     * 지금 회차에서 **실제로 열릴** 큐들. `activeCues()` 의 결과다 — **원본이 아니다.**
     *
     * ★ **큐 배열 원본을 순회하는 코드를 만들지 않는다** (절대 규칙 61 · v21 D15 승계).
     * 그러면 산술이 두 벌이 되고 **콘솔이 말하는 노출 수와 실제가 갈리는데**, 그 어긋남은
     * **아무 오류도 내지 않는다.**
     */
    offerCues = [];
    /** 이미 **연** 큐. **회차가 바뀌면 비운다** (`docs/47` §14 의 9) */
    firedCues = new Set();
    /** 요청은 나갔고 아직 시점이 오지 않았다 */
    pendingOffer = null;
    /** 화면에 떠 있다 */
    offer = null;
    /**
     * 직전 카드가 걷힌 **콘텐츠 축**의 초. **회차마다 리셋된다** (`docs/47` D8).
     *
     * ★ **저장소에 두지 않는 것이 v21 과 갈리는 지점이다.** 광고는 채널 서핑으로 브레이크를
     * **우회할 수 있어서** `StorageAdapter` 가 필요했는데(`RECEIVER_LAST_AD_BREAK_KEY`),
     * 상품 오버레이에는 **우회로 얻을 것이 없다** — 채널을 바꿔도 건너뛴 것이 없다.
     */
    lastOfferClosedAtSec = Number.NEGATIVE_INFINITY;
    /** 이 회차에서 연 개수 */
    offerCountThisItem = 0;
    constructor(offers, host) {
        this.offers = offers;
        this.host = host;
    }
    /**
     * **화면이 상품 오버레이를 그리는 유일한 근거다** (절대 규칙 59).
     *
     * `getCurrentItem()` 은 **카드가 떠 있어도 회차를 돌려준다** — 상품을 돌려주면
     * `send()` 가 `event.productId` 에 제휴사 상품 id 를 실어 그것이 `Event` 로 흘러가고,
     * `EVENT_TYPES` 를 늘리지 않기로 한 것이 아무 소용이 없어진다 (규칙 50 승계).
     */
    view() {
        const current = this.offer;
        if (current === null)
            return null;
        return { cueId: current.cueId, item: current.item, closesAtSec: current.closesAtSec };
    }
    /**
     * 시청자가 닫았거나 눌렀다. **그 큐는 다시 열리지 않는다** — 이미 `firedCues` 에 있다.
     *
     * **`lastOfferClosedAtSec` 을 여기서도 찍는다** — 손으로 닫은 것도 「직전 카드가 걷힌
     * 시각」이다. 안 찍으면 닫자마자 다음 큐가 최소 간격을 무시하고 뜬다.
     */
    dismiss() {
        if (this.offer === null)
            return;
        this.close();
    }
    /**
     * 카드가 걷히기까지 남은 초. 떠 있지 않으면 `0`.
     *
     * ★ **게터로 두고 `OfferView` 에 싣지 않는다** — 화면이 250ms 로 읽는 값이라, 뷰에
     * 실으면 `getOffer()` 이 매 틱 새 객체를 만들고 `useEffect` 의존성이 매번 달라진다
     * (`getAdBreakElapsedSec` 이 이미 같은 이유로 게터다).
     */
    remainingSec() {
        const current = this.offer;
        if (current === null)
            return 0;
        return Math.max(0, Math.ceil(current.closesAtSec - this.host.elapsedSec()));
    }
    /**
     * 상품 쪽 진단. **화면에 증상이 없는 값이 여기 셋 있다** — `firedCues` · `pendingOffer` ·
     * `offerCues` 의 수. 하네스가 읽을 창이 이것뿐이다.
     *
     * ★ **`activeCues()` 가 버린 큐의 수가 드러난다** (`cueCount` 와 `activeCount`).
     * 그것이 「어떤 회차에만 상품이 안 뜬다」의 원인을 화면 밖에서 볼 수 있는 유일한 값이다.
     */
    debugSnapshot() {
        const item = this.host.currentItem();
        const policy = this.host.policy();
        const cues = item?.cues.filter((cue) => cue.isActive) ?? [];
        return {
            hasAdapter: this.offers !== null,
            policy,
            cueCount: cues.length,
            activeCount: this.offerCues.length,
            firedCues: [...this.firedCues],
            pending: this.pendingOffer !== null,
            open: this.offer !== null,
            countThisItem: this.offerCountThisItem,
            lastClosedAtSec: this.lastOfferClosedAtSec,
            correlator: this.host.correlator(),
        };
    }
    /**
     * 상태 여섯을 비운다. **회차가 바뀌는 모든 자리에서 부른다.**
     *
     * ★ **`firedCues` 를 안 비우면 같은 회차를 다시 봤을 때 카드가 하나도 안 뜬다**
     * (`docs/47` §14 의 9). v21 이 `firedCuePoints` 에 대해 겪은 그 자리와 같다.
     */
    reset() {
        const had = this.offer !== null;
        this.offerCues = [];
        this.firedCues.clear();
        this.pendingOffer = null;
        this.offer = null;
        this.lastOfferClosedAtSec = Number.NEGATIVE_INFINITY;
        this.offerCountThisItem = 0;
        // 떠 있던 카드가 있었으면 화면에 걷혔다고 알린다 — 안 알리면 회차가 바뀌었는데
        // 앞 회차의 상품이 그대로 남는다.
        if (had)
            this.host.emit(null);
    }
    /**
     * 이 회차에서 열릴 큐를 정한다. **산술은 `activeCues()` 한 벌뿐이다** (절대 규칙 61).
     *
     * ★ **이어보기 지점보다 앞선 큐를 여기서 미리 발화 처리한다.**
     *
     * 12분부터 이어 보는데 그 앞 큐 셋이 한꺼번에 뜨면 **시청자에게는 광고 세 편보다
     * 나쁘다** — 광고는 끝나기라도 하지만 이것은 화면을 덮는다 (`docs/47` §10).
     *
     * **표시 창(`startSec + displaySec`)까지 지난 것만 넣는다.** 창 안이면 그것은
     * 「지나간 큐」가 아니라 「지금 뜰 큐」이고, 그 구분이 광고에서 돌아온 큐를 살리는
     * 것과 같은 판정이다.
     *
     * ★★ **시작 지점은 호출자가 준다. 여기서 `resumeSecFor()` 를 다시 읽지 않는다**
     * (v23/O16 이 실기로 찾았다).
     *
     * 부르는 자리가 여섯인데 **이어보기 지점에서 시작하는 것은 `start()` 하나뿐**이다 —
     * 나머지 다섯(선택 · 자연 전환 · 복구 · 광고 복귀)은 **전부 0초에서 시작한다.**
     * 여기서 저장된 지점을 읽으면 그 다섯에서 **0초부터 트는 회차의 큐를 「이미 지났다」로
     * 표시해** 카드가 하나도 안 뜬다.
     *
     * **증상이 「그 회차만 상품이 안 뜬다」 하나**이고, 한 번이라도 끝까지 본 회차에서만
     * 나므로 **처음 보는 회차로 시연하면 영원히 드러나지 않는다.**
     *
     * @param startAtSec 이 회차가 **실제로 시작하는 초**. 선택·전환·복구는 `0` 이다.
     */
    arm(item, startAtSec) {
        const policy = this.host.policy();
        if (policy === null || this.offers === null) {
            this.offerCues = [];
            return;
        }
        this.offerCues = activeCues(policy, item.cues, item.durationSec);
        if (startAtSec <= 0)
            return;
        for (const cue of this.offerCues) {
            if (cue.startSec + policy.displaySec <= startAtSec)
                this.firedCues.add(cue.cueId);
        }
    }
    /**
     * `onTimeUpdate` 축(약 4Hz)에서 도는 셋 — **요청 · 표시 · 걷기.**
     *
     * **순서가 뜻을 갖는다.** 걷기가 먼저면 같은 틱에 열린 카드가 그 자리에서 걷히고,
     * 요청이 먼저면 아직 떠 있는 카드 위로 다음 요청이 나간다(게이트가 막지만 헛돈다).
     */
    tick() {
        if (!this.host.isPlaying())
            return;
        // ① 걷기 — 창이 지났으면 먼저 치운다.
        this.closeExpiredOffer();
        // ② 표시 — 받아 둔 것이 있고 시점이 왔으면 띄운다.
        this.showPendingOffer();
        // ③ 요청 — 다음 큐의 리드 타임에 닿았으면 묻는다.
        this.maybeRequestOffer();
    }
    closeExpiredOffer() {
        const current = this.offer;
        if (current === null)
            return;
        if (this.host.elapsedSec() < current.closesAtSec)
            return;
        this.close();
    }
    /**
     * 카드를 걷는다. **`lastOfferClosedAtSec` 을 찍는 자리가 여기 하나다.**
     *
     * 스스로 걷힌 것 · 시청자가 닫은 것 · 광고가 들어온 것이 **전부 이 함수를 지난다** —
     * 셋을 따로 두면 그중 하나에서만 최소 간격이 안 걸린다.
     */
    close() {
        const had = this.offer !== null;
        // **`pendingOffer` 도 함께 버린다** — 아직 안 뜬 카드가 광고 위에 뜨는 것이 같은 결함이다.
        this.pendingOffer = null;
        if (!had)
            return;
        this.offer = null;
        this.lastOfferClosedAtSec = this.host.elapsedSec();
        this.host.emit(null);
    }
    showPendingOffer() {
        const pending = this.pendingOffer;
        const policy = this.host.policy();
        if (pending === null || policy === null)
            return;
        if (this.offer !== null)
            return;
        // **광고 중에는 띄우지 않는다** — `enterAdBreak` 이 이미 버렸지만, 같은 틱에
        // 브레이크가 열리는 경우를 위해 한 번 더 본다 (절대 규칙 64).
        if (this.host.inAdBreak())
            return;
        const elapsedSec = this.host.elapsedSec();
        if (elapsedSec < pending.startSec)
            return;
        const closesAtSec = pending.startSec + policy.displaySec;
        /*
         * ★ **창이 이미 지났으면 띄우지 않고 버린다.** 왕복이 늦었거나 광고가 길었던
         * 경우이고, **재킷을 벗은 다음 장면에 재킷이 뜨는 것**이 이 기능의 신뢰를 가장 빨리
         * 무너뜨린다 (`docs/47` §14 의 7).
         */
        if (elapsedSec > closesAtSec) {
            this.pendingOffer = null;
            return;
        }
        this.pendingOffer = null;
        this.offer = { cueId: pending.cueId, item: pending.item, closesAtSec };
        /*
         * **여기서 발화 표시를 한다** (`docs/47` §8.4 의 흐름).
         *
         * ★ **요청 시점이 아니라 표시 시점인 것이 계약이다.** 광고에 가려져 열리지 못한 큐는
         * 이 줄에 닿지 않으므로 `firedCues` 에 들어가지 않고, **광고가 끝나고 아직 창 안이면
         * 그때 뜬다** (절대 규칙 64 · `docs/47` §14 의 10).
         */
        this.firedCues.add(pending.cueId);
        this.offerCountThisItem += 1;
        // **노출은 `Event` 로 가지 않는다** (절대 규칙 55 승계) — 브로커의 트래킹 URL 로 간다.
        this.offers?.track(pending.item.tracking.impression);
        this.host.emit(this.view());
    }
    maybeRequestOffer() {
        const policy = this.host.policy();
        if (policy === null)
            return;
        const elapsedSec = this.host.elapsedSec();
        for (const cue of this.offerCues) {
            if (this.firedCues.has(cue.cueId))
                continue;
            // 아직 리드 타임에 닿지 않았다 — `offerCues` 가 시점 순이므로 여기서 멈춘다.
            if (elapsedSec < cue.startSec - OFFER_REQUEST_LEAD_SEC)
                return;
            // **창이 이미 지난 큐는 건너뛴다.** 표시할 자리가 없다.
            if (elapsedSec > cue.startSec + policy.displaySec)
                continue;
            void this.openOffer(cue);
            return;
        }
    }
    /**
     * ★ **카드가 열리는 유일한 관문이다** (`docs/47` §8.4).
     *
     * **가드가 여섯이고 이름으로 부른다** — 번호를 붙이지 않는다: §3.3 의 「뒤집을 수 없는
     * 규칙 넷」이 이미 ①~④ 를 쓰고 있어 겹친다.
     *
     * **정책 없음 · 이미 떠 있거나 대기 중 · 광고 중 · 이미 연 큐 · 회차 상한 · 최소 간격.**
     *
     * 흩으면 「어떤 경로에서만 최소 간격이 안 걸린다」 같은 것이 생기고, **그런 구멍은
     * 정확히 그 경로를 밟는 시연에서만 드러난다.**
     *
     * **실패는 조용하다.** `emit('error')` 하지 않는다 — 상품이 안 뜨는 것은 시청자에게
     * 사고가 아니다 (`docs/47` §8.6 · `resolveNextUrl` 의 `catch` 가 그 선례다).
     */
    async openOffer(cue) {
        const policy = this.host.policy();
        // **정책 없음** — 어댑터를 안 준 배포도 여기서 멈춘다.
        if (policy === null || this.offers === null)
            return;
        // **이미 떠 있거나 대기 중** — 동시에 뜨는 카드는 하나다 (절대 규칙 64). 재귀가 없다.
        if (this.offer !== null || this.pendingOffer !== null)
            return;
        /*
         * **광고 중** — 그리고 `firedCues` 에 넣지 않으므로 복귀 뒤 창 안이면 그때 뜬다.
         *
         * ★ **`pendingAd` 를 여기서 보지 않는다** (v23/O16 이 실기로 찾았다).
         *
         * 그 값은 「광고 중」이 아니라 **「다음 브레이크를 미리 받아 뒀다」**이고, 코어는
         * 컷보다 **한참 앞서** 받아 둔다 — 하네스 픽스처(컷 20초)에서 회차 **0.7초**에 이미
         * 참이었다. 그것으로 막으면 **미드롤 앞의 콘텐츠 구간 전체에서 카드가 한 장도 안
         * 뜬다.** 광고를 켠 채널에서만 그렇고, 증상은 「이 채널만 상품이 안 뜬다」 하나다.
         *
         * **막지 않아도 카드가 광고 위에 뜨지 않는다** — 실제로 브레이크가 열리는 순간
         * `enterAdBreak` 이 `closeOffer()` 로 대기분까지 버리고, `showPendingOffer` 와
         * 이 함수의 `await` 뒤 재검사가 **둘 다 `adBreak` 을 다시 본다.** 막는 자리는
         * 셋이고 전부 「지금 광고 중인가」를 묻는다 (절대 규칙 64).
         */
        if (this.host.inAdBreak())
            return;
        // **이미 연 큐**
        if (this.firedCues.has(cue.cueId))
            return;
        // **회차 상한** — `0` 은 제한 없음이다.
        if (policy.maxPerItem > 0 && this.offerCountThisItem >= policy.maxPerItem)
            return;
        // **최소 간격** — 거르는 것이지 미루는 것이 아니다. 콘텐츠 축의 초로 잰다.
        if (this.host.elapsedSec() < this.lastOfferClosedAtSec + policy.minGapSec)
            return;
        const current = this.host.currentItem();
        const ctx = this.host.requestContext();
        // **`correlator` 는 코어가 이미 들고 있다** — 광고 요청이 그 모양이고, 새 헬퍼를
        // 만들지 않는다. 없으면 프록시가 같은 응답을 돌려준다 (v21 §14 의 15).
        const correlator = this.host.nextCorrelator();
        const items = await this.offers.requestOffer({
            brokerUrl: policy.brokerUrl,
            categoryId: cue.categoryId,
            taxonomy: cue.taxonomy,
            hint: cue.hint,
            channelId: ctx.channelId,
            productId: current?.productId ?? '',
            cueId: cue.cueId,
            positionSec: Math.max(0, Math.floor(this.host.elapsedSec())),
            currency: CURRENCY,
            /*
             * ★ **채널이 정하는 언어다. 기기의 UI 언어가 아니다** (v21 D26 승계).
             *
             * **`Channel.offerLanguage` 컬럼이 없어 광고 언어를 빌린다** — 설계 §7.2 가
             * 「채널이 정한다」고 적었는데 §2.1 이 그 칸을 만들지 않았다(`OFFER_LANGUAGES`
             * 의 ★). **광고를 끈 영어 채널은 `ko` 로 떨어진다** — 알려진 한계다.
             */
            lang: ctx.lang,
            deviceId: ctx.deviceId,
            playlistId: ctx.playlistId,
            correlator,
        }, { timeoutMs: OFFER_REQUEST_TIMEOUT_MS });
        // **매칭 없음 · 실패 · 타임아웃 — 셋이 같은 길이다.** 새 분기가 없다.
        const item = items[0];
        if (item === undefined)
            return;
        /*
         * ★ **늦게 온 응답을 버린다** (`docs/47` §8.4 의 ★ · §14 의 7).
         *
         * 왕복 동안 시점이 지났으면 **그 장면은 이미 끝났다.** 재킷을 벗은 다음 장면에
         * 재킷이 뜨는 것이 이 기능의 신뢰를 가장 빨리 무너뜨린다.
         *
         * **타임아웃(5초)이 리드 타임(10초)보다 짧은 것이 이 가드가 매번 걸리지 않는
         * 근거다** — 길게 두면 상품이 한 번도 안 뜨는데 요청·응답은 정상으로 보인다.
         * 그 관계는 `offer.test.ts` 의 단언 한 줄이 지킨다.
         */
        if (this.host.elapsedSec() > cue.startSec + policy.displaySec)
            return;
        // 기다리는 사이에 브레이크가 열렸거나 다른 카드가 떴을 수 있다. 한 번 더 본다.
        if (this.host.inAdBreak() || this.offer !== null || this.pendingOffer !== null)
            return;
        // 기다리는 사이에 회차가 바뀌었으면 그 큐는 이 회차의 것이 아니다.
        if (!this.offerCues.some((candidate) => candidate.cueId === cue.cueId))
            return;
        this.pendingOffer = { cueId: cue.cueId, startSec: cue.startSec, item };
    }
}
//# sourceMappingURL=offer-cues.js.map